import { GameStatus } from '@betterpool/api-types/games-service';

import type { PlayerDetail } from '~/domains/games/interfaces/player-detail.interface';
import type { PlayerPoolMember } from '~/domains/contest/domains/services/interfaces/player-pool-member.interface';
import type { PickForEntry } from '~/domains/contest/domains/services/interfaces/picks-for-entry.interface';

import type { PlayerInfo } from '../../types/player';

import parseNonScheduledTeamSport from './parseNonScheduledTeamSport';
import parseScheduledTeamSport from './parseScheduledTeamSport';
import parseBasePlayerInfoTeamSport from './parseBasePlayerInfoTeamSport';

const parseResultsQB = (playerActivities: Record<string, number>) => [
  {
    label: null,
    value: [
      playerActivities.passing_completions ?? '-',
      playerActivities.passing_attempts ?? '-',
    ].join('/'),
  },
  {
    label: 'YDS',
    value: playerActivities.passing_yards ?? '-',
  },
  {
    label: 'TD',
    value: playerActivities.passing_touchdowns ?? '-',
  },
];

const parseResultsRB = (playerActivities: Record<string, number>) => [
  {
    label: 'ATT',
    value: playerActivities.rushing_attempts ?? '-',
  },
  {
    label: 'YDS',
    value: playerActivities.rushing_yards ?? '-',
  },
  {
    label: 'TD',
    value: playerActivities.rushing_touchdowns ?? '-',
  },
];

const parseResultsWRorTE = (playerActivities: Record<string, number>) => [
  {
    label: 'REC',
    value: playerActivities.receiving_receptions ?? '-',
  },
  {
    label: 'YDS',
    value: playerActivities.receiving_yards ?? '-',
  },
  {
    label: 'TD',
    value: playerActivities.receiving_touchdowns ?? '-',
  },
];

const parseFootballActivities = (pickWithLiveData: PickForEntry) => {
  const playerActivities = pickWithLiveData.playerActivities ?? {};

  // eslint-disable-next-line local-rules/require-let-annotation
  let parser;
  switch (pickWithLiveData.attributes.position) {
    case 'QB':
      parser = parseResultsQB;
      break;
    case 'RB':
      parser = parseResultsRB;
      break;
    case 'WR':
    case 'TE':
      parser = parseResultsWRorTE;
      break;
    default:
      parser = () => [];
  }

  return {
    meta: parser(playerActivities),
  };
};

const parseNonScheduledFootball = (
  pickWithLiveData: PickForEntry | PlayerDetail
): Partial<PlayerInfo> => {
  const isLive = pickWithLiveData.attributes.gameStatus === GameStatus.IN_PROGRESS;
  const playerActivities = parseFootballActivities(pickWithLiveData as PickForEntry);

  return {
    ...playerActivities,
    gameTime: (() => {
      if (!isLive) {
        return 'FINAL';
      }

      if (!pickWithLiveData.attributes.summary) return '-';
      return pickWithLiveData.attributes.summary.overtime
        ? 'OT'
        : `${pickWithLiveData.attributes.summary.clock} Q${pickWithLiveData.attributes.summary.quarter}`;
    })(),
  };
};

const parseNonScheduled = (pickWithLiveData: PickForEntry | PlayerDetail) => ({
  ...parseNonScheduledFootball(pickWithLiveData),
  ...parseNonScheduledTeamSport(pickWithLiveData),
});

const parseFootballPlayerInfo = (
  pick: PlayerPoolMember,
  pickWithLiveData?: PickForEntry
): Partial<PlayerInfo> => ({
  ...(pick.attributes.gameStatus === GameStatus.SCHEDULED || !pickWithLiveData
    ? parseScheduledTeamSport(pick)
    : parseNonScheduled(pickWithLiveData)),
  ...parseBasePlayerInfoTeamSport(pick),
});

export { parseNonScheduled as parseFootballPlayerInfoAfterGameStart };

export default parseFootballPlayerInfo;
